import { Plus, Shield, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { PORTLESS_PROTOCOLS, type SecgroupRule, type RuleType, type RuleProtocol } from '@/api/opennebula';
import styles from './SecgroupRuleEditor.module.css';

const PROTOCOLS: RuleProtocol[] = ['TCP', 'UDP', 'ICMP', 'ICMPv6', 'IPSEC', 'ALL'];
const isPortless = (p: RuleProtocol) => PORTLESS_PROTOCOLS.includes(p);

/** Common rule presets (mirror legacy SecgroupRules quick-add). */
const PRESETS: Record<RuleType, { label: string; rule: Omit<SecgroupRule, 'rule_type'> }[]> = {
  INBOUND: [
    { label: 'SSH (22)', rule: { protocol: 'TCP', range: '22', ip: '', ref_type: 'SSH' } },
    { label: 'HTTP (80)', rule: { protocol: 'TCP', range: '80', ip: '', ref_type: 'HTTP' } },
    { label: 'HTTPS (443)', rule: { protocol: 'TCP', range: '443', ip: '', ref_type: 'HTTPS' } },
    { label: 'MySQL (3306)', rule: { protocol: 'TCP', range: '3306', ip: '', ref_type: 'Custom' } },
    { label: 'PostgreSQL (5432)', rule: { protocol: 'TCP', range: '5432', ip: '', ref_type: 'Custom' } },
    { label: 'All TCP', rule: { protocol: 'TCP', range: '1:65535', ip: '', ref_type: 'ALL TCP' } },
  ],
  OUTBOUND: [
    { label: 'All TCP', rule: { protocol: 'TCP', range: '1:65535', ip: '', ref_type: 'ALL TCP' } },
    { label: 'All UDP', rule: { protocol: 'UDP', range: '1:65535', ip: '', ref_type: 'ALL UDP' } },
    { label: 'ICMP', rule: { protocol: 'ICMP', range: '', ip: '', ref_type: 'ICMP' } },
  ],
};

/**
 * Inbound/outbound firewall-rule editor (re-skin of the legacy MUI SecgroupRules).
 * Controlled: holds the full flat rule list, grouped into two tables by `rule_type`.
 */
export function SecgroupRuleEditor({
  value,
  onChange,
}: {
  value: SecgroupRule[];
  onChange: (rules: SecgroupRule[]) => void;
}) {
  const setRule = (idx: number, patch: Partial<SecgroupRule>) =>
    onChange(value.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRule = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addRule = (rule_type: RuleType) =>
    onChange([...value, { rule_type, protocol: 'TCP', range: '', ip: '', ref_type: 'Custom' }]);
  const addPreset = (rule_type: RuleType, label: string) => {
    const p = PRESETS[rule_type].find((x) => x.label === label);
    if (p) onChange([...value, { rule_type, ...p.rule }]);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {(['INBOUND', 'OUTBOUND'] as RuleType[]).map((type) => {
        const rows = value
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => r.rule_type === type);
        return (
          <div key={type}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-2)' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
                {type === 'INBOUND' ? 'Inbound' : 'Outbound'} rules
              </span>
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                <Select value="" onValueChange={(v) => addPreset(type, v)}>
                  <SelectTrigger size="sm" style={{ minWidth: 130 }}><SelectValue placeholder="Quick add…" /></SelectTrigger>
                  <SelectContent>
                    {PRESETS[type].map((p) => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} onClick={() => addRule(type)}>
                  Add rule
                </Button>
              </div>
            </div>
            {rows.length === 0 ? (
              <EmptyState icon={<Shield size={20} />} title={`No ${type.toLowerCase()} rules yet`} description={`Add a rule to control ${type.toLowerCase()} traffic.`} />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                <div className={styles.ruleHeader}>
                  <span>Protocol</span>
                  <span>Port / range</span>
                  <span>CIDR</span>
                  <span />
                </div>
                {rows.map(({ r, i }) => (
                  <div key={i} className={styles.ruleRow}>
                    <Select value={r.protocol} onValueChange={(v) => setRule(i, { protocol: v as RuleProtocol })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROTOCOLS.map((p) => (
                          <SelectItem key={p} value={p}>
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={r.range ?? ''}
                      onChange={(e) => setRule(i, { range: e.target.value })}
                      placeholder={isPortless(r.protocol) ? 'n/a' : '22 or 1:65535'}
                      disabled={isPortless(r.protocol)}
                    />
                    {/* Clear any kept `size` on edit — a non-aligned range no longer applies once the user retypes the network. */}
                    <Input value={r.ip ?? ''} onChange={(e) => setRule(i, { ip: e.target.value, size: undefined })} placeholder="0.0.0.0/0 (any)" />
                    <IconButton variant="ghost" size="sm" aria-label="Remove rule" onClick={() => removeRule(i)}>
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
